/**
 * MasterData Management Page
 * CRUD operations for master data categories
 */

import React, { useState, useEffect } from "react";
import { apiRequest } from "../api/apiClient";
import { showSuccessToast, showErrorToast, showConfirm } from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import { usePageTitle } from "../contexts/PageTitleContext";
import BusyOverlay from "../components/common/BusyOverlay";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import "../styles/pages/MasterData.css";

interface MasterDataItem {
  id: number;
  category: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

type AccordionSection =
  | "gender"
  | "document"
  | "documenttype"
  | "position"
  | "currency"
  | "paymentmethod"
  | "contracttype"
  | "trackingreminderschedule"
  | "expirereminderschedule";

export const MasterData: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [masterData, setMasterData] = useState<MasterDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedSections, setExpandedSections] = useState<
    Set<AccordionSection>
  >(new Set());

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalCategory, setModalCategory] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Master Data Management", "Manage master data categories");
    fetchMasterData();
  }, [setPageTitle]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiRequest("/MasterData", { method: "GET" });
      const data = await response.json();
      setMasterData(Array.isArray(data) ? data : (data as any)?.data || []);
    } catch (err: any) {
      console.error("Error fetching master data:", err);
      setError(err?.message || "Failed to load master data");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: AccordionSection) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedSections(
      new Set<AccordionSection>([
        "gender",
        "document",
        "documenttype",
        "position",
        "currency",
        "paymentmethod",
        "contracttype",
        "trackingreminderschedule",
        "expirereminderschedule",
      ]),
    );
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const getDataByCategory = (category: string) => {
    return masterData
      .filter((item) => item.category.toLowerCase() === category.toLowerCase())
      .sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const openCreateModal = (category: string) => {
    setModalCategory(category);
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const openEditModal = (item: MasterDataItem) => {
    setModalCategory(item.category);
    setEditingId(item.id);
    setFormData({
      name: item.name,
      description: item.description || "",
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
    });
    setModalCategory("");
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setValidationErrors({ name: REQUIRED_FIELD_MESSAGE });
      return;
    }

    setValidationErrors({});

    try {
      setLoading(true);

      if (editingId) {
        // Update existing item
        await apiRequest(`/MasterData/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: editingId,
            category: modalCategory,
            name: formData.name,
            description: formData.description,
            isActive: true,
          }),
        });
        await fetchMasterData();
        closeModal();
        showSuccessToast("Master data updated successfully");
      } else {
        // Create new item
        await apiRequest("/MasterData", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            category: modalCategory,
            code: formData.name.toLowerCase().replace(/\s+/g, "-"),
            displayOrder: 0,
            isActive: true,
          }),
        });
        await fetchMasterData();
        closeModal();
        showSuccessToast("Master data created successfully");
      }
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const confirmed = await showConfirm(
      "Delete Master Data?",
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      "Yes, delete",
      "Cancel",
    );

    if (confirmed) {
      try {
        setLoading(true);
        await apiRequest(`/MasterData/${id}`, { method: "DELETE" });
        await fetchMasterData();
        showSuccessToast("Master data deleted successfully");
      } catch (err: any) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderCategorySection = (
    category: string,
    section: AccordionSection,
    icon: string,
    displayLabel?: string,
  ) => {
    const categoryData = getDataByCategory(category);
    const isExpanded = expandedSections.has(section);
    const title = displayLabel || category;

    return (
      <div
        key={section}
        className={`accordion-item ${isExpanded ? "expanded" : ""}`}
      >
        <button
          className="accordion-header"
          onClick={() => toggleSection(section)}
          aria-expanded={isExpanded}
        >
          <div className="accordion-title">
            <span className="accordion-icon">{icon}</span>
            {title}
            {categoryData.length > 0 && (
              <span className="badge-count">{categoryData.length}</span>
            )}
          </div>
          <span className="accordion-arrow">{isExpanded ? "▼" : "▶"}</span>
        </button>
        {isExpanded && (
          <div className="accordion-content">
            {/* Add Button - Top Right */}
            <div className="accordion-content-header">
              <button
                className="btn btn--primary"
                onClick={() => openCreateModal(category)}
                disabled={loading}
              >
                +
              </button>
            </div>

            {/* Data Table */}
            {categoryData.length > 0 ? (
              <div
                className="master-data-table-container"
                style={{ width: "100%" }}
              >
                <table className="master-data-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th style={{ width: "100px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.description || "-"}</td>
                        <td>
                          <span
                            className={`status-badge ${item.isActive ? "status-active" : "status-inactive"}`}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon"
                              title="Edit"
                              onClick={() => openEditModal(item)}
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-icon"
                              title="Delete"
                              onClick={() => handleDelete(item.id, item.name)}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state-small">
                <p>No {title.toLowerCase()} data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />

      <div className="main-content">
        <div className="masterdata-page">
          <BusyOverlay isVisible={loading} message="Loading master data..." />

          {/* Accordion Controls */}
          <div className="settings-accordion-controls">
            <button type="button" className="btn-link" onClick={expandAll}>
              Expand All
            </button>
            <span className="divider">|</span>
            <button type="button" className="btn-link" onClick={collapseAll}>
              Collapse All
            </button>
          </div>

          <div className="masterdata-content">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="accordion-container">
              {renderCategorySection("Gender", "gender", "👤")}
              {renderCategorySection(
                "DocumentType",
                "documenttype",
                "📄",
                "Document Types",
              )}
              {renderCategorySection("Currency", "currency", "💰")}
              {renderCategorySection("Payment Method", "paymentmethod", "💳")}
            </div>
          </div>

          {/* Modal for Create/Edit */}
          {showModal && (
            <div className="modal-overlay" onClick={closeModal}>
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2>
                    {editingId ? "Edit" : "Add"} {modalCategory}
                  </h2>
                  <button className="modal-close" onClick={closeModal}>
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <div
                    className={`form-group floating-label ${validationErrors.name ? "form-group--error" : ""}`}
                  >
                    <input
                      type="text"
                      className={`form-control ${validationErrors.name ? "form-control--error" : ""}`}
                      id="masterdata-name"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (validationErrors.name) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.name;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder=" "
                      autoFocus
                    />
                    <label htmlFor="masterdata-name">Name *</label>
                    {validationErrors.name && (
                      <div className="field-error">{validationErrors.name}</div>
                    )}
                  </div>
                  <div className="form-group floating-label">
                    <textarea
                      className="form-control"
                      id="masterdata-description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder=" "
                      rows={3}
                    />
                    <label htmlFor="masterdata-description">Description</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn--secondary"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn--primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : editingId ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
